<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          indeterminate
          color="primary"
          class="mt-12"
          v-if="loading"
        />

        <v-card width="650px" v-else>
          <v-card-title>
            <span class="headline">{{ editedItem.title }}</span>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="remove"
              color="red"
              v-if="editedItem.is_removable"
            >
              <v-icon small class="mr-2">mdi-delete</v-icon>
              {{ $t("newsfeed.newsFeedDetails.delete") }}
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-container>
              <v-row dense>
                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.title")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.title }}</v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.shortPreview")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.snippet }}</v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.contentURL")
                    }}</v-card-title>
                    <v-card-subtitle> {{ editedItem.url }}</v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.imageThumbnail")
                    }}</v-card-title>
                  </v-card>
                  <direct-file-upload
                    :label="$t('newsfeed.newsFeedDetails.imageThumbnail')"
                    :details-view="true"
                    asset-type="image_asset"
                    :files="
                      editedItem.main_image !== null &&
                      Object.keys(editedItem.main_image).length !== 0
                        ? [editedItem.main_image]
                        : []
                    "
                  >
                    <template v-slot:default="{ file }">
                      <v-menu v-model="menu" offset-x>
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            style="cursor: pointer; position: absolute"
                            v-bind="attrs"
                            v-on="on"
                            @mouseover="menu = true"
                            @mouseleave="menu = false"
                          >
                            <v-icon class="mr-2">mdi-file-image</v-icon>
                            {{ file.name }}
                          </div>
                        </template>

                        <v-card class="pa-2">
                          <v-list>
                            <v-list-item>
                              <v-list-item-content>
                                <v-img
                                  max-height="150"
                                  max-width="250"
                                  :src="
                                    typeof file.src === 'undefined'
                                      ? getResizedImageSrc(file.link)
                                      : file.src
                                  "
                                ></v-img>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-card>
                      </v-menu>
                    </template>
                  </direct-file-upload>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.publishDate")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{
                        $moment(editedItem.date_published)
                          .toISOString()
                          .substr(0, 10)
                      }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-text>
                      <v-checkbox
                        class="mb-0 pb-0"
                        v-model="editedItem.pinned"
                        :label="$t('newsfeed.newsFeedDetails.alwaysOnTop')"
                        color="primary"
                        disabled
                      ></v-checkbox>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.audience")
                    }}</v-card-title>
                    <v-card-subtitle>
                      {{
                        $tc(
                          "newsfeed.newsFeedDetails.boats",
                          editedItem.estimated.count,
                          { count: editedItem.estimated.count }
                        )
                      }}</v-card-subtitle
                    >
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1">{{
                      $t("newsfeed.newsFeedDetails.brand")
                    }}</v-card-title>
                    <v-card-subtitle>
                      <v-chip
                        v-bind:key="index"
                        v-for="(
                          group, index
                        ) in editedItem.audience_crm_groups_details"
                        class="mx-1 my-1"
                      >
                        <span>{{ group.name }}</span>
                      </v-chip>
                    </v-card-subtitle>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="12" md="12" lg="12">
                  <v-card elevation="0">
                    <v-card-title class="subtitle-1"
                      >{{ $t("newsfeed.newsFeedDetails.connectivity") }}
                    </v-card-title>
                    <v-card-subtitle>
                      <span class="text-capitalize">{{
                        editedItem.audience_connectivity
                      }}</span>
                    </v-card-subtitle>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              data-cy="modelsCancelBtn"
              text
              @click="close()"
              >{{ $t("newsfeed.newsFeedDetails.cancel") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-layout>
      <delete-dialog
        :dialog="deleteDialog.active"
        :item="deleteDialog.title"
        @agree="handleRemove"
        v-show="false"
      />
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api";
import snackbar from "@/utils/snackbar";
import DirectFileUpload from "@/components/miscellaneous/DirectFileUpload.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";

export default {
  components: {
    DirectFileUpload,
    DeleteDialog,
  },
  data: () => ({
    loading: true,
    editedItem: {
      date_published: null,
      audience_crm_groups: [],
      audience_crm_groups_details: [],
      audience_connectivity: "ALL",
      estimated: { count: 0, index: 0 },
      url: "",
    },
    menu: false,
    deleteDialog: { active: false, title: "" },
  }),
  created() {
    Promise.all([this.fetchNewsFeed(), this.fetchBrands()]).then((values) => {
      let newsFeedHandler = (data, brands) => {
        this.editedItem = {
          ...this.editedItem,
          ...data,
          audience_connectivity:
            data.audience_connectivity !== null
              ? data.audience_connectivity[0]
              : null,
          audience_crm_groups_details: brands.filter((brand) =>
            data.audience_crm_groups.includes(brand.uuid)
          ),
        };
      };

      newsFeedHandler(values[0].data, values[1].data);
      this.loading = false;
    });
  },
  methods: {
    getResizedImageSrc(src) {
      if (!src) return null;

      const extansion =
        src.substring(src.lastIndexOf(".") + 1, src.length) || src;

      return `${src.split(extansion)[0]}250x150.${extansion}`;
    },
    fetchNewsFeed() {
      return api.getNewsFeed(this.$route.params.id);
    },

    fetchBrands() {
      return api.groupsAutocomplete({
        search: "",
        include_all_brands: true,
        exclude_supergroup: true,
      });
    },

    close() {
      this.$router.push({
        name: "newsfeed",
      });
    },

    handleRemove(agreed) {
      if (agreed) {
        api.deleteNewsFeed(this.deleteDialog.item.uuid).then((resp) => {
          snackbar.success(
            this.$t("utils.snackbar.success.successfullyDeleted")
          );
          this.close();
          this.deleteDialog = { active: false, title: "", item: null };
        });
      }
      this.deleteDialog = { active: false, title: "", item: null };
    },

    remove() {
      this.deleteDialog = {
        active: true,
        title: this.editedItem.title,
        item: this.editedItem,
      };
    },
  },
};
</script>
<style></style>
